import { Component, Inject, ChangeDetectionStrategy, computed, signal } from '@angular/core'
import { FieldTree, form, maxLength } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { firstValueFrom } from 'rxjs'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import {
  annaKirjausehdotusAiAsetuksetUri,
  KIRJAUSEHDOTUS_AI_ASETUKSET_LISAOHJE_MAX,
  KIRJAUSEHDOTUS_AI_ASETUKSET_OLETUS,
  KIRJAUSEHDOTUS_KATEGORIA_OTSIKOT,
  KirjausehdotusAiAsetukset,
} from '../../_jaettu-lemonator/model/llm-kirjausehdotus-asetukset'
import { KirjausehdotusBackfillPyynto, KirjausehdotusBackfillVastaus } from '../../_jaettu-lemonator/model/llm-kirjausehdotus-backfill'
import { LLM_KIRJAUSEHDOTUS_DETERMINISTISET_KATEGORIAT, LLM_KIRJAUSEHDOTUS_KATEGORIAT, LlmKirjausehdotusKategoria } from '../../_jaettu-lemonator/model/llm-kirjausehdotus-testaus'

export interface KirjausAiAsetuksetDialogData {
  asiakas: Asiakas
}

interface AsetuksetLomake {
  kaytossa: boolean
  lisaohje: string
  automaattihyvaksynta: boolean
}

const LISAOHJE_PLACEHOLDER = `Esimerkki: Asiakkaalla on kaksi autoa, joiden kuluja kirjataan eri tileille ja ALV-käsittelyillä:

- Pakettiauto, ABC-123 (diesel): 100 % yritystoiminnan käytössä, täysi ALV-vähennysoikeus. Kirjataan tilille 7500.
- Henkilöauto, DEF-456 (bensa): osittain yksityiskäytössä, ei ALV-vähennystä (0 %). Kirjataan tilille 7501.

Asiakas merkitsee yleensä tositteisiin kummasta autosta on kyse. Käytä tätä tietoa ensisijaisesti. Jos tositteesta ei löydy mainintaa autosta, yritä päätellä auto kuitin tiedoista.`

@Component({
  templateUrl: './kirjaus.ai-asetukset.dialog.html',
  styleUrls: ['./kirjaus.ai-asetukset.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjausAiAsetuksetDialog {
  lomake = form(signal<AsetuksetLomake>({ kaytossa: false, lisaohje: '', automaattihyvaksynta: false }), polku => {
    maxLength(polku.lisaohje, KIRJAUSEHDOTUS_AI_ASETUKSET_LISAOHJE_MAX)
  })
  kategoriat: LlmKirjausehdotusKategoria[] = LLM_KIRJAUSEHDOTUS_KATEGORIAT.filter(kategoria => !LLM_KIRJAUSEHDOTUS_DETERMINISTISET_KATEGORIAT.includes(kategoria))
  otsikot = KIRJAUSEHDOTUS_KATEGORIA_OTSIKOT
  lisaohjeMax = KIRJAUSEHDOTUS_AI_ASETUKSET_LISAOHJE_MAX
  lisaohjePlaceholder = LISAOHJE_PLACEHOLDER
  readonly ladataan = signal(true)
  readonly tallennetaan = signal(false)
  readonly backfillMaara = signal<number | null>(null)
  readonly backfillKaynnissa = signal(false)
  readonly backfillKaynnistetaan = signal(false)

  private readonly _valitutKategoriat = signal(new Set<LlmKirjausehdotusKategoria>())
  private _takautuvat = KIRJAUSEHDOTUS_AI_ASETUKSET_OLETUS.takautuvat
  private readonly _alkuperaiset = signal<KirjausehdotusAiAsetukset | null>(null)

  readonly valitutMaara = computed(() => this._valitutKategoriat().size)
  readonly kaikkiValittu = computed(() => this._valitutKategoriat().size === this.kategoriat.length)
  readonly kaikkiOsittain = computed(() => this._valitutKategoriat().size > 0 && this._valitutKategoriat().size < this.kategoriat.length)
  readonly backfillNappiDisabloitu = computed(() => this.backfillKaynnistetaan() || this.backfillKaynnissa() || !this.backfillMaara() || this.ladataan() || !this.lomake.kaytossa().value())

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KirjausAiAsetuksetDialogData,
    private _dialogRef: MatDialogRef<KirjausAiAsetuksetDialog, boolean>,
    private _dialog: MatDialog,
    private _firebase: FirebaseLemonator,
    private _formValidationService: FormValidationService,
    private _snackbar: MatSnackBar,
  ) {
    void this._lataaAsetukset()
    void this._lataaBackfillTila()
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  onKategoriaValittu(kategoria: LlmKirjausehdotusKategoria): boolean {
    return this._valitutKategoriat().has(kategoria)
  }

  vaihdaKategoria(kategoria: LlmKirjausehdotusKategoria) {
    this._valitutKategoriat.update(valitut => {
      const uudet = new Set(valitut)
      if (uudet.has(kategoria)) {
        uudet.delete(kategoria)
      } else {
        uudet.add(kategoria)
      }
      return uudet
    })
  }

  vaihdaKaikki() {
    this._valitutKategoriat.set(this.kaikkiValittu() ? new Set() : new Set(this.kategoriat))
  }

  onMuuttunut(): boolean {
    const alkuperaiset = this._alkuperaiset()
    if (!alkuperaiset) {
      return false
    }
    return JSON.stringify(this._rakennaAsetukset()) !== JSON.stringify(alkuperaiset)
  }

  async kaynnistaBackfill() {
    if (this.backfillNappiDisabloitu()) {
      return
    }

    const data: AreYouSureDialogData = {
      text: `Tehdään AI-kirjausehdotukset ${this.backfillMaara()} olemassa olevalle hyväksymättömälle kirjaukselle vanhimmasta uusimpaan. Jo hyväksyttyihin kirjauksiin ei kosketa.`,
      header: 'Käynnistä takautuva ajo',
      rightAction: 'Käynnistä',
    }
    const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!sure) {
      return
    }

    this.backfillKaynnistetaan.set(true)
    try {
      const vastaus = await this._firebase.functionsCall<KirjausehdotusBackfillPyynto, KirjausehdotusBackfillVastaus>('llmKirjausehdotusBackfill', {
        op: 'aloita',
        asiakasAvain: this.data.asiakas.avain,
      })
      if (vastaus.e === 'ei-kaytossa') {
        this._snackbar.open('AI-kirjausehdotukset eivät ole käytössä asiakkaalla. Tallenna asetukset ensin.', 'OK', { duration: 5000 })
      } else if (vastaus.e === 'jo-kaynnissa') {
        this.backfillKaynnissa.set(true)
        this._snackbar.open('Takautuva ajo on jo käynnissä.', 'OK', { duration: 5000 })
      } else if (vastaus.e) {
        this._snackbar.open('Takautuvan ajon käynnistys epäonnistui. Yritä uudelleen.', 'OK', { duration: 5000 })
      } else if (!vastaus.maara) {
        this.backfillMaara.set(0)
        this._snackbar.open('Ei kirjauksia joille ehdotuksen voisi tehdä.', 'OK', { duration: 5000 })
      } else {
        this.backfillKaynnissa.set(true)
        this._snackbar.open(`Takautuva ajo käynnistetty (${vastaus.maara} kirjausta).`, 'OK', { duration: 5000 })
      }
    } catch (err) {
      console.error('Takautuvan ajon käynnistys epäonnistui', err)
      this._snackbar.open('Takautuvan ajon käynnistys epäonnistui. Yritä uudelleen.', 'OK', { duration: 5000 })
    } finally {
      this.backfillKaynnistetaan.set(false)
    }
  }

  async tallenna() {
    if (this.tallennetaan() || this.ladataan()) {
      return
    }
    if (this.lomake().invalid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this.tallennetaan.set(true)
    try {
      await this._firebase.firestoreSetData(annaKirjausehdotusAiAsetuksetUri(this.data.asiakas.avain), this._rakennaAsetukset(), { merge: true })
      this._dialogRef.close(true)
    } catch {
      this._snackbar.open('Asetusten tallennus epäonnistui. Yritä uudelleen.', 'OK', { duration: 5000 })
      this.tallennetaan.set(false)
    }
  }

  private _rakennaAsetukset(): KirjausehdotusAiAsetukset {
    const lisaohjeTeksti = this.lomake.lisaohje().value().trim()
    return {
      kaytossa: this.lomake.kaytossa().value(),
      kategoriat: this.kaikkiValittu() ? null : [...this._valitutKategoriat()].sort(),
      takautuvat: this._takautuvat,
      lisaohje: lisaohjeTeksti ? lisaohjeTeksti : null,
      automaattihyvaksynta: this.lomake.automaattihyvaksynta().value(),
    }
  }

  private async _lataaAsetukset() {
    try {
      const ladattu = await this._firebase.firestoreDoc<KirjausehdotusAiAsetukset>(annaKirjausehdotusAiAsetuksetUri(this.data.asiakas.avain)).get()
      const asetukset: KirjausehdotusAiAsetukset = { ...KIRJAUSEHDOTUS_AI_ASETUKSET_OLETUS, ...(ladattu ?? {}) }
      this._takautuvat = asetukset.takautuvat
      asetaArvo(this.lomake.kaytossa, asetukset.kaytossa)
      asetaArvo(this.lomake.lisaohje, asetukset.lisaohje ?? '')
      asetaArvo(this.lomake.automaattihyvaksynta, asetukset.automaattihyvaksynta)
      const valitut = new Set(this._valitutKategoriat())
      if (asetukset.kategoriat === null) {
        for (const kategoria of this.kategoriat) {
          valitut.add(kategoria)
        }
      } else {
        for (const kategoria of asetukset.kategoriat) {
          if (this.kategoriat.includes(kategoria)) {
            valitut.add(kategoria)
          }
        }
      }
      this._valitutKategoriat.set(valitut)
      this._alkuperaiset.set(this._rakennaAsetukset())
    } catch (err) {
      console.error('AI-asetusten lataus epäonnistui', err)
      this._snackbar.open('AI-asetusten lataus epäonnistui. Sulje dialogi ja yritä uudelleen.', 'OK', { duration: 5000 })
    } finally {
      this.ladataan.set(false)
    }
  }

  private async _lataaBackfillTila() {
    try {
      const vastaus = await this._firebase.functionsCall<KirjausehdotusBackfillPyynto, KirjausehdotusBackfillVastaus>('llmKirjausehdotusBackfill', {
        op: 'laskeMaara',
        asiakasAvain: this.data.asiakas.avain,
      })
      if (!vastaus.e) {
        this.backfillMaara.set(vastaus.maara ?? 0)
        this.backfillKaynnissa.set(!!vastaus.kaynnissa)
      }
    } catch (err) {
      console.error('Takautuvan ajon tilan lataus epäonnistui', err)
    }
  }
}
